import type {EventColorMap} from "~/components/beatPlanningCalendar/beatPlanningCalendar";

export type CalendarViewMode =
    | "timeGridDay"
    | "timeGridFourDay"
    | "timeGridWeek"
    | "dayGridMonth"
    | "scheduleList"
    | "multiMonthYear";

/** Matches `useCheckMobile` (`innerWidth < 768`). */
export const MOBILE_CALENDAR_VIEWPORT_MEDIA_QUERY = "(max-width: 767px)";

export const MOBILE_DEFAULT_CALENDAR_VIEW_MODE: CalendarViewMode = "scheduleList";
export const DESKTOP_DEFAULT_CALENDAR_VIEW_MODE: CalendarViewMode = "timeGridWeek";

export function readIsMobileCalendarViewportSync(): boolean {
    if (typeof window === "undefined") {
        return false;
    }
    return window.matchMedia(MOBILE_CALENDAR_VIEWPORT_MEDIA_QUERY).matches;
}

export function getDefaultCalendarViewModeForViewport(isMobile: boolean): CalendarViewMode {
    return isMobile ? MOBILE_DEFAULT_CALENDAR_VIEW_MODE : DESKTOP_DEFAULT_CALENDAR_VIEW_MODE;
}

export function resolveNonYearCalendarViewMode(mode: CalendarViewMode): CalendarViewMode {
    if (mode === "scheduleList" || mode === "multiMonthYear") {
        return "dayGridMonth";
    }
    return mode;
}

type LegendItem = {
    type: string;
    label: string;
    description?: string;
    color?: EventColorMap;
};

type ToolbarHandlers = {
    prev: () => void;
    next: () => void;
    today: () => void;
    setView: (view: CalendarViewMode) => void;
};

type ToolbarState = {
    title: string;
    currentViewMode: CalendarViewMode;
    legendItems: Array<LegendItem>;
    handlers: ToolbarHandlers | null;
};

let state: ToolbarState = {
    title: "",
    currentViewMode: DESKTOP_DEFAULT_CALENDAR_VIEW_MODE,
    legendItems: [],
    handlers: null,
};

const listeners = new Set<() => void>();

function emit() {
    listeners.forEach((listener) => listener());
}

export function subscribeCalendarToolbar(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function getCalendarToolbarState(): ToolbarState {
    return state;
}

export function getCalendarToolbarTitle(): string {
    return state.title;
}

export function getCalendarToolbarViewMode(): CalendarViewMode {
    return state.currentViewMode;
}

export function getCalendarToolbarLegendItems(): Array<LegendItem> {
    return state.legendItems;
}

export function getCalendarToolbarHandlers(): ToolbarHandlers | null {
    return state.handlers;
}

export function setCalendarToolbarState(next: Partial<ToolbarState>) {
    const nextState: ToolbarState = {...state, ...next};
    const hasChanged =
        nextState.title !== state.title ||
        nextState.currentViewMode !== state.currentViewMode ||
        nextState.legendItems !== state.legendItems ||
        nextState.handlers !== state.handlers;
    if (hasChanged === false) {
        return;
    }
    state = nextState;
    emit();
}

export function clearCalendarToolbarHandlers() {
    if (state.handlers === null) {
        return;
    }
    state = {...state, handlers: null};
    emit();
}
